import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator, Pressable, NativeEventEmitter,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesome } from '@expo/vector-icons';
import Markdown from 'react-native-markdown-display';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import PriorityFlag from '../16pxIcons/PriorityFlag';
import SeverityIcon from '../16pxIcons/SeverityIcon';
import Button from '../Button';
import AlertIcon24 from '../24pxIcons/AlertIcon24';
import AnomalyIcon from '../24pxIcons/AnomalyIcon';
import InsightIcon from '../24pxIcons/InsightIcon';
import isMobile from '../IsMobile';
import FlagIcon from '../FlagIcon';
import InProgressIcon16 from '../16pxIcons/InProgressIcon16';
import PausedIcon from '../12pxIcons/PausedIcon';
import ResolvedIcon16 from '../16pxIcons/ResolvedIcon16';
import ReopenedIcon from '../16pxIcons/ReopenedIcon';
import getPriorityColor from '../PriorityColorScheme';
import {
  Notification, getNotificationHeaderText, getNotificationTime, getCategoryScreen,
  getNotificationHeaderColor, getNotificationBorderColor, getNotificationStatusBackgroundColor,
  getNotificationStatusColor, getNotificationStatusText, getStatusFlagIconBorderColor,
} from './data';
import BellIcon from '../BellIcon';
import markNotificationsRead from '../../requests/Notifications/MarkNotificationsRead';
import MarkSeenIcon from '../24pxIcons/MarkSeenIcon';
import { updateBadgeCount, updateBuildingId, updateBuildingName } from '../../redux/selectedBuildingSlice';
import MarkUnseenIcon from '../24pxIcons/MarkUnseenIcon';
import AlertModal from '../AlertModal';
import {
  HappyScoreCategory, HappyScoreCategoryTitles, NotificationType, TicketStatus,
} from '../../constants/Types';
import { updateSelectedTicket } from '../../redux/selectedTicketSlice';
import PressableStyled from '../PressableStyled';
import getTicket from '../../requests/Tickets/GetTicket';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import { Building } from '../../screens/LocationManagement/components/AddBuildingModal';

const getItemIcon = (itemType: string, ticketStatus?: TicketStatus) => {
  if (itemType === NotificationType.ALERT) return <AlertIcon24 />;
  if (itemType === NotificationType.ANOMALY) return <AnomalyIcon />;
  if (itemType === NotificationType.INSIGHT) return <InsightIcon />;
  if (itemType === NotificationType.UPDATE || itemType === NotificationType.RAISED) {
    return <View style={[
      styles.ticketIconContainer,
      {
        backgroundColor: getNotificationStatusBackgroundColor(ticketStatus),
        borderColor: getStatusFlagIconBorderColor(ticketStatus),
      },
    ]}>
      <FlagIcon color={getNotificationStatusColor(ticketStatus)} />
    </View>;
  }
  return <View style={styles.ticketIconContainer}>
    <BellIcon />
  </View>;
};

const getStatusIcon = (ticketStatus: TicketStatus) => {
  if (ticketStatus === TicketStatus.INPROGRESS) return <InProgressIcon16 />;
  if (ticketStatus === TicketStatus.PAUSED) return <PausedIcon />;
  if (ticketStatus === TicketStatus.COMPLETED) return <ResolvedIcon16 />;
  if (ticketStatus === TicketStatus.REOPENED) return <ReopenedIcon />;
  return null;
};

interface NotificationItemProps extends Notification {
  expand?: () => void,
  buttonHidden?: boolean,
  hideModal?: () => void,
}

const NotificationItem = ({
  type, timestamp, title, description, priority, severity, ticketId, dropdownView, status, category,
  buildingName, style, buildingId, isRead, id, expanded, expand, buttonHidden = false, hideModal,
}: NotificationItemProps) => {
  // Hiding severity until we have a use case for it
  const showSeverity: boolean = false;

  const routeName: string = useRoute().name;
  const priorityIndex: number = priority - 1;
  const severityIndex: number = severity - 1;
  const [isUnread, setIsUnread] = useState<boolean>(!isRead);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const isTicketNotification: boolean = ticketId && (type === NotificationType.UPDATE
    || type === NotificationType.RAISED);
  const isInsightNotification: boolean = type === NotificationType.ALERT
    || type === NotificationType.INSIGHT || type === NotificationType.ANOMALY;
  const [readStatusLoading, setReadStatusLoading] = useState<boolean>(false);
  const [navigationLoading, setNavigationLoading] = useState<boolean>(false);
  const navigation = useNavigation();
  const badgeCount: number = useSelector(
    (state: { selectedBuilding: { badgeCount: number } }) => state.selectedBuilding.badgeCount,
  ) ?? 0;
  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const dispatch = useDispatch();
  const emitter = isMobile ? null : new NativeEventEmitter();

  useEffect(() => setIsUnread(!isRead), [isRead]);

  useEffect(() => {
    emitter?.addListener('mark_all_notifications_read', () => {
      setIsUnread(false);
    });
    emitter?.addListener('mark_notification_read', (params: { id: string, isUnread: boolean }) => {
      if (id === params.id) setIsUnread(!params.isUnread);
    });
  }, []);

  const markRead: () => void = () => {
    setReadStatusLoading(true);
    markNotificationsRead(id, isUnread).then((res) => {
      if (res) {
        emitter?.emit('mark_notification_read', { id, isUnread });
        setIsUnread(!isUnread);
        dispatch(updateBadgeCount(Math.max(0, badgeCount + (isUnread ? -1 : 1))));
      }
      setReadStatusLoading(false);
    }).catch(() => setReadStatusLoading(false));
  };

  const action = () => {
    const selectedBuilding: Building | null = (buildings && buildingId)
      ? buildings[buildingId] : null;
    if (isTicketNotification && !selectedBuilding) {
      setAlertMessage('The building that this ticket was reported from no longer exists');
      return;
    }
    if (selectedBuilding) {
      dispatch(updateBuildingId(buildingId));
      dispatch(updateBuildingName(selectedBuilding.Name));
    }
    if (isUnread) markRead();
    if (isTicketNotification) {
      setNavigationLoading(true);
      getTicket(ticketId).then((res) => {
        if (!res || res.isError) {
          setAlertMessage('Unable to load ticket details');
        } else {
          dispatch(updateSelectedTicket({ ...res, pinned: true }));
          navigation.navigate(AppScreen.TICKETS as any);
        }
      }).catch(() => {
        setAlertMessage('Unable to load ticket details');
      }).finally(() => setNavigationLoading(false));
    } else if (getCategoryScreen(category)) {
      if (isMobile) {
        navigation.navigate('MenuStack' as any, {
          screen: getCategoryScreen(category),
          params: {
            openInsights: true,
            notificationIds: [id],
          },
          initial: false,
        });
      } else {
        navigation.navigate(
          getCategoryScreen(category) as any,
          { openInsights: true, notificationIds: [id] },
        );
      }
    }
    if (hideModal) hideModal();
  };

  const statusIndicator = (dropdown: boolean) => <View style={[
    styles.priorityContainer,
    dropdown && styles.dropdownPriorityContainer,
    { borderColor: getNotificationStatusColor(status) },
    { backgroundColor: getNotificationStatusBackgroundColor(status) },
  ]}>
    <View style={styles.statusIconContainer}>
      {getStatusIcon(status)}
    </View>
    <Text style={[styles.priorityText, { color: getNotificationStatusColor(status) }]}>
      {getNotificationStatusText(status)}
    </Text>
  </View>;

  const showActionButton: boolean = !buttonHidden
    && ((isTicketNotification || (HappyScoreCategoryTitles[category]
      && getCategoryScreen(category) && (!isMobile || isInsightNotification || isTicketNotification)
      && (routeName !== 'Home' || category !== HappyScoreCategory.HAPPY_SCORE)))
      || (isInsightNotification && isMobile && category === HappyScoreCategory.ENERGY));

  const CHAR_LIMIT: number = 150;

  return <View
    style={[
      styles.container, dropdownView && styles.dropdownViewContainer, style,
      { borderColor: getNotificationBorderColor(type) },
    ]}
    testID={dropdownView ? 'notificaiton-in-notif-center' : 'notification-details'}
  >
    <View style={styles.dropdownViewHeader}>
      <View style={[styles.headerContainer, !!getItemIcon(type) && styles.headerWithIcon]}>
        <View style={styles.iconContainer}>
          {getItemIcon(type, status)}
          {isUnread && <View style={styles.unreadIndicator} />}
        </View>
        <View style={{ flex: 1 }}>
          {!!getNotificationHeaderText(type)
            && <Text style={[styles.headerText, { color: getNotificationHeaderColor(type) }]}>
              {getNotificationHeaderText(type)}
            </Text>}
          {buildingName && <Text
            style={[styles.headerText, dropdownView && styles.dropdownViewHeaderText]}
          >
            {'Building: '}
            <Text selectable>{buildingName}</Text>
          </Text>}
          {ticketId?.length > 0 && isTicketNotification
            && <View style={{ flexDirection: 'row' }}><Text style={[styles.headerText, { color: getNotificationHeaderColor(type) }]}>
              {'Ticket ID: '}
            </Text><Text
              selectable
              style={styles.ticketId}
              testID='ticket-id'
            >
                {ticketId}
              </Text></View>}
        </View>
      </View>
      {dropdownView && <View style={[Style.row, styles.dropdownStatusSection]}>
        {status && isTicketNotification
          && status !== TicketStatus.REPORTED && statusIndicator(true)}
        {(priority && isTicketNotification)
          ? <View style={[
            styles.priorityContainer, styles.dropdownPriorityContainer,
            { borderColor: getPriorityColor(priorityIndex) },
          ]}>
            <PriorityFlag color={getPriorityColor(priorityIndex)} />
            <Text style={[styles.priorityText, { color: getPriorityColor(priorityIndex) }]}>
              {`P${priority}`}
            </Text>
          </View> : <></>}
      </View>}
      {(!!severity && isInsightNotification && showSeverity) && <View style={[
        styles.priorityContainer, styles.dropdownPriorityContainer,
        { borderColor: getPriorityColor(severityIndex) },
      ]}>
        <SeverityIcon color={getPriorityColor(severityIndex)} />
        <Text style={[styles.priorityText, { color: getPriorityColor(severityIndex) }]}>
          {`S${severity}`}
        </Text>
      </View>}
    </View>
    <Text
      style={styles.title}
      selectable
    >
      {title}
    </Text>
    {!dropdownView && <View style={styles.attributesContainer}>
      {!!priority && (type === NotificationType.UPDATE || type === NotificationType.RAISED)
        && <View style={[
          styles.priorityContainer,
          { borderColor: getPriorityColor(priorityIndex) },
        ]}>
          <PriorityFlag color={getPriorityColor(priorityIndex)} />
          <Text style={[styles.priorityText, { color: getPriorityColor(priorityIndex) }]}>
            {`P${priority}`}
          </Text>
        </View>}
      {status && isTicketNotification && status !== TicketStatus.REPORTED && statusIndicator(false)}
    </View>}
    <View style={styles.content}>
      <View style={styles.textContainer}>
        <View>
          <Markdown
            children={(!expand || expanded || !dropdownView) ? description
              : `${description.substring(0, CHAR_LIMIT)}${description.length > CHAR_LIMIT ? '...'
                : ''}`}
            style={{ body: styles.description }} />
          {expand && dropdownView && description?.trim().length > CHAR_LIMIT && <Pressable
            style={(state: { hovered: boolean, pressed: boolean }) => [
              styles.expandButton, state.hovered && Style.hovered, state.pressed && Style.pressed,
            ]}
            onPress={expand}
          >
            <Text style={[styles.priorityText, styles.expandButtonText]}>
              {expanded ? 'Collapse' : 'Expand'}
            </Text>
          </Pressable>}
        </View>
        {dropdownView && <View style={isMobile ? null : Style.row}>
          {showActionButton && <Button
            title={isTicketNotification ? 'Open ticket'
              : `Go to ${HappyScoreCategoryTitles[category]}`}
            affirmative={!isMobile}
            onPress={action}
            style={styles.dropdownViewButton}
            testID='open-ticket-btn'
            children={navigationLoading ? <ActivityIndicator
              size='small'
              color='#FFF'
              style={styles.activityIndicator}
            /> : null}
          />}
          {isMobile && <Text
            style={styles.timestamp}
            selectable
          >
            {getNotificationTime(timestamp)}
          </Text>}
        </View>}
        <View style={isMobile ? null : Style.row}>
          {(!isMobile || !dropdownView) && <View style={Style.row}>
            <PressableStyled
              onPress={markRead}
              style={Style.row}
              testID={`mark-as-${isUnread ? '' : 'un'}seen-btn`}
            >
              {isUnread ? <MarkSeenIcon color={styles.markReadText.color} />
                : <MarkUnseenIcon color={styles.markReadText.color} />}
              <Text style={styles.markReadText}>{`Mark as ${isUnread ? 'seen' : 'unseen'}`}</Text>
            </PressableStyled>
            {readStatusLoading && <ActivityIndicator />}
            {isMobile && !showActionButton && <Text
              style={styles.timestamp}
              selectable
            >
              {getNotificationTime(timestamp)}
            </Text>}
          </View>}
          <View style={isMobile ? null : Style.row}>
            {!isMobile && <Text
              style={styles.timestamp}
              selectable
            >
              {getNotificationTime(timestamp)}
            </Text>}
            {!dropdownView && <View style={isMobile ? null : Style.row}>
              {showActionButton && <Button
                affirmative
                title={isTicketNotification ? 'Open ticket'
                  : `Go to ${HappyScoreCategoryTitles[category]}`}
                children={navigationLoading ? <ActivityIndicator
                  size='small'
                  color='#FFF'
                  style={styles.activityIndicator}
                /> : <FontAwesome
                  name='angle-right'
                  color='#FFF'
                  size={20}
                  style={styles.buttonIcon}
                />}
                onPress={action}
                style={styles.actionButton}
                testID='open-ticket-btn'
              />}
              {isMobile && showActionButton && <Text
                style={styles.timestamp}
                selectable
              >
                {getNotificationTime(timestamp)}
              </Text>}
            </View>}
          </View>
        </View>
      </View>
    </View>
    <AlertModal
      modalVisible={!!alertMessage}
      setModalVisible={() => setAlertMessage(null)}
      title='Error'
      message={alertMessage}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
    borderWidth: 1,
    borderRadius: 16,
    borderColor: Colors.light.hereworksDarkBlue,
    marginBottom: 8,
    backgroundColor: '#FFF',
  },
  dropdownViewContainer: {
    paddingHorizontal: 12,
  },
  dropdownViewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerWithIcon: {
    marginBottom: 8,
  },
  headerText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  title: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginTop: 8,
  },
  content: {
    justifyContent: 'space-between',
  },
  description: {
    marginVertical: 8,
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack500,
  },
  dropdownDescription: {
    marginTop: 12,
  },
  timestamp: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginTop: isMobile ? 8 : undefined,
  },
  actionButton: {
    marginStart: isMobile ? 0 : 16,
    marginTop: isMobile ? 16 : 0,
    paddingHorizontal: 12,
    paddingVertical: 5,
    alignSelf: isMobile ? 'flex-start' : 'flex-end',
    flex: undefined,
  },
  priorityContainer: {
    marginTop: 4,
    paddingHorizontal: 4,
    marginEnd: 4,
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  dropdownPriorityContainer: {
    marginTop: 0,
    marginEnd: 0,
    marginStart: 4,
  },
  priorityText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    marginStart: 2,
  },
  iconContainer: {
    marginEnd: 8,
  },
  ticketIconContainer: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlack100,
  },
  ticketId: {
    color: Colors.light.hereworksBlue500,
    flex: 1,
  },
  dropdownViewButton: {
    marginStart: 0,
    paddingHorizontal: 12,
    paddingVertical: 5,
    flex: undefined,
    alignSelf: 'flex-start',
    marginTop: 16,
    marginBottom: isMobile ? 0 : 16,
  },
  statusIconContainer: {
    width: 16,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  attributesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  textContainer: {
    flex: isMobile ? undefined : 1,
  },
  dropdownStatusSection: {
    position: 'absolute',
    end: 0,
    top: 0,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  expandButton: {
    alignSelf: 'flex-end',
  },
  expandButtonText: {
    color: Colors.light.hereworksBlue500,
  },
  unreadIndicator: {
    position: 'absolute',
    top: 0,
    start: 0,
    width: 10,
    aspectRatio: 1,
    borderRadius: 5,
    backgroundColor: Colors.light.hereworksRed500,
  },
  markReadText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginStart: 3,
    marginEnd: 10,
  },
  buttonIcon: {
    marginStart: 10,
  },
  dropdownViewHeaderText: {
    marginTop: 4,
  },
  activityIndicator: {
    marginStart: 12,
  },
});

export default NotificationItem;
